/**
 * Karofi add-to-cart effects: product image flies into the header cart, the cart count bumps,
 * the button shows "Added" and a toast confirms the item (its View cart button opens the drawer).
 * Also handles card quick-add buttons ([data-quick-atc]) and the product page Buy now button.
 */
(() => {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const ICON_CHECK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

	const visible = (el) => {
		const r = el.getBoundingClientRect();
		return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
	};
	const cartIcon = () => Array.from(document.querySelectorAll('[id="cart-drawer-toggle"]')).find(visible) || null;

	const sourceImage = (source) => {
		if (!source) return null;
		const scopes = [
			source.closest('.lux-card, .kcc-card, .product-card'),
			source.closest('.thb-product-detail, .product-detail, .main-product, [id^="MainProduct"]'),
			document.querySelector('.thb-product-detail, .product-images, .product-gallery')
		].filter(Boolean);
		for (const scope of scopes) {
			const img = Array.from(scope.querySelectorAll('img')).find((i) => visible(i) && i.getBoundingClientRect().width > 60);
			if (img) return img;
		}
		return null;
	};

	const inView = (r) => r.bottom > 40 && r.top < window.innerHeight - 40;

	const fly = (img, imageUrl, startEl) => new Promise((resolve) => {
		const target = cartIcon();
		if (reduceMotion || (!img && !imageUrl)) return resolve();
		// Start from the product image when it is on screen, otherwise pop the image up over the clicked button.
		let from = img && img.getBoundingClientRect();
		const popped = !from || !inView(from);
		if (popped) {
			const b = startEl ? startEl.getBoundingClientRect() : null;
			const cx = b && inView(b) ? b.left + b.width / 2 : window.innerWidth / 2;
			const cy = b && inView(b) ? b.top + b.height / 2 - 70 : window.innerHeight / 2;
			from = { left: cx - 70, top: cy - 70, width: 140, height: 140 };
		}
		const to = target && target.getBoundingClientRect().top >= 0 ? target.getBoundingClientRect() : { left: window.innerWidth - 48, top: 16, width: 28, height: 28 };
		const size = Math.min(from.width, from.height, 220);
		const clone = document.createElement('img');
		clone.className = 'kfx-fly';
		clone.alt = '';
		clone.src = img ? (img.currentSrc || img.src) : imageUrl;
		const x0 = from.left + from.width / 2 - size / 2;
		const y0 = from.top + from.height / 2 - size / 2;
		Object.assign(clone.style, { left: x0 + 'px', top: y0 + 'px', width: size + 'px', height: size + 'px' });
		document.body.appendChild(clone);
		const dx = to.left + to.width / 2 - (x0 + size / 2);
		const dy = to.top + to.height / 2 - (y0 + size / 2);
		const end = 24 / size;
		const frames = [
			{ transform: 'translate(0,0) scale(1)', opacity: 1, borderRadius: '16px', offset: popped ? 0.25 : 0 },
			{ transform: `translate(${dx * 0.35}px, ${dy * 0.35 - 90}px) scale(0.6)`, opacity: 1, borderRadius: '50%', offset: popped ? 0.6 : 0.45 },
			{ transform: `translate(${dx}px, ${dy}px) scale(${end})`, opacity: 0.4, borderRadius: '50%', offset: 1 }
		];
		if (popped) frames.unshift({ transform: 'translate(0,0) scale(0.3)', opacity: 0, borderRadius: '16px', offset: 0 });
		const anim = clone.animate(frames, { duration: popped ? 1100 : 850, easing: 'cubic-bezier(.55,0,.35,1)' });
		const done = () => {
			clone.remove();
			if (target) {
				target.classList.remove('kfx-bump');
				void target.offsetWidth;
				target.classList.add('kfx-bump');
			}
			resolve();
		};
		anim.onfinish = done;
		anim.oncancel = done;
	});

	let toastTimer;
	const toast = (title) => {
		let el = document.querySelector('.kfx-toast');
		if (!el) {
			el = document.createElement('div');
			el.className = 'kfx-toast';
			el.setAttribute('role', 'status');
			el.setAttribute('aria-live', 'polite');
			el.innerHTML = `<span class="kfx-toast__ok">${ICON_CHECK}</span><span class="kfx-toast__text"></span><button type="button" class="kfx-toast__btn">View cart</button>`;
			el.querySelector('.kfx-toast__btn').addEventListener('click', () => {
				el.classList.remove('is-shown');
				openDrawer();
			});
			document.body.appendChild(el);
		}
		el.querySelector('.kfx-toast__text').textContent = `${title} added to cart`;
		el.classList.remove('is-shown');
		void el.offsetWidth;
		el.classList.add('is-shown');
		clearTimeout(toastTimer);
		toastTimer = setTimeout(() => el.classList.remove('is-shown'), 3800);
	};

	const markAdded = (button) => {
		if (!button) return;
		const label = button.querySelector('span:not(.loading-overlay)') || button;
		if (!button.dataset.kfxLabel) button.dataset.kfxLabel = label.innerHTML;
		label.innerHTML = `<span class="kfx-added">${ICON_CHECK}Added</span>`;
		button.classList.add('kfx-is-added');
		clearTimeout(button._kfxTimer);
		button._kfxTimer = setTimeout(() => {
			label.innerHTML = button.dataset.kfxLabel;
			button.classList.remove('kfx-is-added');
		}, 2200);
	};

	const openDrawer = () => {
		const drawer = document.getElementById('Cart-Drawer');
		if (drawer && typeof drawer.open === 'function') drawer.open();
		else window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
	};

	/** Runs the full effect. Resolves when the image has landed in the cart. */
	window.karofiCartFx = ({ source, button, item } = {}) => {
		const title = (item && (item.product_title || item.title)) || '';
		const imageUrl = item && (item.featured_image ? item.featured_image.url : item.image);
		markAdded(button || (source && source.querySelector && source.querySelector('[type="submit"]')));
		return fly(sourceImage(button || source), imageUrl, button || source).then(() => {
			if (title) toast(title);
		});
	};

	const renderSections = (sections) => {
		if (!sections) return;
		const parse = (html, selector) => {
			const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
			return node ? node.innerHTML : null;
		};
		const drawer = document.getElementById('Cart-Drawer');
		if (drawer && sections['cart-drawer']) {
			const html = parse(sections['cart-drawer'], '.cart-drawer');
			if (html !== null) {
				drawer.innerHTML = html;
				if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
			}
		}
		if (sections['cart-bubble']) {
			const html = parse(sections['cart-bubble'], '.thb-item-count');
			if (html !== null) document.querySelectorAll('[id="cart-drawer-toggle"] .thb-item-count').forEach((c) => { c.innerHTML = html; });
		}
	};

	const addUrl = () => ((window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add') + '.js';

	// Card quick add: <button data-quick-atc data-variant-id="…">
	document.addEventListener('click', (e) => {
		const button = e.target.closest('[data-quick-atc]');
		if (!button || button.disabled) return;
		e.preventDefault();
		if (button.classList.contains('is-loading')) return;
		button.classList.add('is-loading');
		fetch(addUrl(), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }], sections: 'cart-drawer,cart-bubble', sections_url: window.location.pathname })
		})
			.then((r) => r.json())
			.then((res) => {
				if (res.status) {
					toast(res.description || res.message || 'Sorry, this item');
					return;
				}
				renderSections(res.sections);
				const item = (res.items && res.items[0]) || res;
				return window.karofiCartFx({ button, item });
			})
			.catch(() => { window.location.href = button.dataset.url || '/cart'; })
			.finally(() => button.classList.remove('is-loading'));
	});

	// Product page Buy now: add the selected variant, then go straight to checkout.
	document.addEventListener('click', (e) => {
		const button = e.target.closest('[data-kbuy-now]');
		if (!button || button.disabled) return;
		const form = button.closest('form') || document.querySelector('form[action*="/cart/add"]');
		if (!form) return;
		e.preventDefault();
		const data = new FormData(form);
		if (!data.get('id')) {
			const id = form.querySelector('[name="id"]');
			if (id) data.set('id', id.value);
		}
		button.classList.add('is-loading');
		fetch(addUrl(), { method: 'POST', headers: { Accept: 'application/json' }, body: data })
			.then((r) => r.json())
			.then((res) => {
				if (res.status) throw new Error(res.description || res.message);
				window.location.href = '/checkout';
			})
			.catch((err) => {
				button.classList.remove('is-loading');
				toast(err.message || 'Please try again');
			});
	});

	// Move the Shopee / Lazada buttons from the buy box into the 3-button row under Add to cart.
	const mountMarket = () => {
		document.querySelectorAll('[data-kbuy]').forEach((row) => {
			if (row.dataset.kbuyMounted) return;
			const scope = row.closest('.thb-product-detail, .product-information, section') || document;
			const market = scope.querySelector('.kbb__market') || document.querySelector('.kbb__market');
			if (!market) return;
			market.querySelectorAll('.kbb__btn').forEach((a) => row.appendChild(a));
			market.hidden = true;
			row.classList.add('kbuy--3');
			row.dataset.kbuyMounted = '1';
		});
	};
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountMarket);
	else mountMarket();
})();
